// LinkVault "Vault Design" theme engine (cool-pass Tier-L — ENGINE, not
// aesthetic). This module is the single seam between a persisted theme shape
// and the rendered LinkVault. It is deliberately value-agnostic: it works with
// ANY bg_color / wallpaper / accent the user picks today, and leaves a clean
// hook for Fable's curated PRESETS later (resolvePreset) without touching the
// renderer or the persistence layer.
//
// Shape persisted on UserProfile.vault_theme (backend, additive nullable JSON):
//   { bg_color?: string, wallpaper?: string, accent?: string, preset?: string }
// NULL / undefined / {} === NEUTRAL PASSTHROUGH: today's exact LinkVault look.

// The neutral default == the gradient the LinkVault renders today when no theme
// is set. Keeping it here (not hard-coded in the renderer) means "unset" and
// "explicitly neutral" resolve identically, and Fable can later swap the
// default by editing one place.
export const NEUTRAL_VAULT_BACKGROUND = `
  linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 50%),
  conic-gradient(from 90deg at 49.13% 50%, #F5EEDE -41.73deg, #CCFEFF 37.8deg, #F7FED5 140.4deg, #FDCFFE 216deg, #F5EEDE 318.27deg, #CCFEFF 397.8deg)
`;

// The full-page LinkVault uses a slightly different (taller) gradient today.
export const NEUTRAL_VAULT_BACKGROUND_FULLPAGE = `
  linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 70%),
  conic-gradient(from 90deg at 49.13% 50%, #F5EEDE -41.73deg, #CCFEFF 37.8deg, #F7FED5 140.4deg, #FDCFFE 216deg, #F5EEDE 318.27deg, #CCFEFF 397.8deg)
`;

// Neutral accent == the LinkVault's existing CTA card border/text neutral.
// (Fable owns the "alive" accent; this is just a passthrough.)
export const NEUTRAL_VAULT_ACCENT = null; // null => fall back to today's hard-coded values

// FABLE SEAM: preset id -> concrete theme. Empty today; Fable drops curated
// presets in here. Because resolveVaultTheme() runs every theme through this,
// a saved { preset: "midnight" } will "just work" the day a preset is added —
// no renderer or API change needed.
const VAULT_PRESETS = {
  // "midnight": { bg_color: "#0B0B12", wallpaper: "...", accent: "#9B5AF8" },
};

export const resolvePreset = (presetId) =>
  (presetId && VAULT_PRESETS[presetId]) || null;

// ── CSS-injection guards ────────────────────────────────────────────────────
// All theme values are USER-SUPPLIED and get interpolated into inline CSS, so
// each must be validated before it reaches the DOM. These guards are only ever
// reached when the theming flag is ON (the renderer never calls these resolvers
// when the flag is OFF), so the flag-OFF path stays byte-identical.

// Strict color allowlist: hex (#RGB/#RRGGBB/#RRGGBBAA), rgb()/rgba(),
// hsl()/hsla(), oklch(). Each pattern is FULLY ANCHORED (^…$) and its inner
// character class only permits the digits/separators legal inside that form —
// no `;`/`{`/`}`/`<`/`>`/quotes/`url(` can ride along, so a payload like
// `red);filter:blur()` or `#000;}body{…}` cannot match any of these.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR = /^rgba?\(\s*[0-9.,%\s/]+\)$/i;
const HSL_COLOR = /^hsla?\(\s*[0-9.,%\s/deg]+\)$/i;
const OKLCH_COLOR = /^oklch\(\s*[0-9.,%\s/]+\)$/i;

// True when `v` is a single, safe CSS color literal we are willing to inline.
// The anchored allowlist above is the guard; the length cap is a cheap upfront
// bound. (No separate "dangerous char" pre-check: rgb/hsl/oklch legitimately
// contain parens, and the anchored patterns already exclude every breakout
// character.)
export const isSafeCssColor = (v) => {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s || s.length > 50) return false;
  return (
    HEX_COLOR.test(s) ||
    RGB_COLOR.test(s) ||
    HSL_COLOR.test(s) ||
    OKLCH_COLOR.test(s)
  );
};

// A gradient string (e.g. linear-gradient(...)/conic-gradient(...)) used as a
// background. It legitimately contains parens/commas/percentages, so it cannot
// pass isSafeCssColor; instead we allow the gradient functions but still reject
// anything that could break out of the declaration (`;`/`{`/`}`/`<`/`>`/quotes/
// control chars/`url(`).
// eslint-disable-next-line no-control-regex
const BG_BREAKOUT = /[;{}<>"']|url\s*\(|[\x00-\x1f]/i;
const isSafeGradient = (v) =>
  typeof v === "string" &&
  v.length <= 2000 &&
  /(linear|radial|conic)-gradient\s*\(/i.test(v) &&
  !BG_BREAKOUT.test(v);

// True for any background value we'll inline: a safe solid color OR a safe
// gradient string.
const isSafeCssBackground = (v) => isSafeCssColor(v) || isSafeGradient(v);

// Validate a user-supplied http(s) wallpaper URL. Returns the URL string when
// it is a well-formed http(s) URL free of CSS-breakout characters
// (quotes/`)`/`;`/`<`/`>`/newlines & control chars), else null.
const sanitizeHttpWallpaper = (raw) => {
  if (typeof raw !== "string" || raw.length > 2000) return null;
  if (/["'()<>;]|[\x00-\x1f]/.test(raw)) return null; // eslint-disable-line no-control-regex
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.href;
};

// Normalise an arbitrary persisted value into a safe theme object.
export const normalizeVaultTheme = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  // If a preset is referenced, the preset's values are the base; any explicit
  // free-form fields the user also set win over the preset (override seam).
  const preset = resolvePreset(raw.preset);
  const base = preset || {};
  return {
    ...base,
    ...(raw.bg_color ? { bg_color: raw.bg_color } : {}),
    ...(raw.wallpaper ? { wallpaper: raw.wallpaper } : {}),
    ...(raw.accent ? { accent: raw.accent } : {}),
    ...(raw.preset ? { preset: raw.preset } : {}),
  };
};

// Resolve the CSS `background` value for the LinkVault container.
// Priority: explicit wallpaper (gradient/image url) > solid bg_color.
// Returns NULL when no background is set (neutral) so the caller keeps today's
// EXACT look — a true passthrough, not a re-painted "neutral" surface.
export const resolveVaultBackground = (theme /* , opts */) => {
  const t = normalizeVaultTheme(theme);
  if (t.wallpaper) {
    // wallpaper may be a CSS gradient string or an image url
    if (typeof t.wallpaper === "string" && /^https?:\/\//i.test(t.wallpaper)) {
      // http(s) image URL: validate & only inline if it can't break out of the
      // url("...") string. Reject (fall through to bg_color/neutral) otherwise.
      const safeUrl = sanitizeHttpWallpaper(t.wallpaper);
      if (safeUrl) return `url("${safeUrl}") center / cover no-repeat`;
    } else if (isSafeCssBackground(t.wallpaper)) {
      // gradient string (or a solid color used as a wallpaper)
      return t.wallpaper;
    }
    // Unsafe wallpaper: fall through to bg_color / neutral passthrough.
  }
  if (t.bg_color && isSafeCssBackground(t.bg_color)) return t.bg_color;
  return null;
};

// Resolve the accent color (CTA card border / emphasis). Null => caller keeps
// its existing default, so unset is a true passthrough.
export const resolveVaultAccent = (theme) => {
  const t = normalizeVaultTheme(theme);
  // accent is inlined into `border: 1px solid <accent>`, so only allow a strict
  // single-color literal; anything else falls back to the neutral default.
  return isSafeCssColor(t.accent) ? t.accent : NEUTRAL_VAULT_ACCENT;
};

// gordon-0917-themes-popup-add-business-box-and-linkvault (ADDED SPEC,
// 2026-09-17 10:40 PT): "Match the Gallery" for LinkVault. Read FIRST, before
// resolveVaultBackground/resolveVaultAccent run: when vault_theme.match_gallery
// is true, the LinkVault's OWN bg_color/wallpaper/accent are left completely
// untouched in storage (never overwritten, never cleared -- so unchecking the
// toggle restores them exactly) and this function instead maps the token
// theme the Gallery itself uses onto LinkVault's legacy {bg_color, wallpaper,
// accent} shape: the gallery's resolved background becomes the vault's
// wallpaper, and the gallery's button fill (its most prominent accent color)
// becomes the vault's accent. When OFF (or no gallery theme set), the vault's
// own stored theme passes through unchanged.
export const resolveEffectiveVaultTheme = (vaultTheme, galleryTheme, resolveGalleryBg, resolveGalleryButtonFill) => {
  const own = normalizeVaultTheme(vaultTheme);
  if (!vaultTheme || !vaultTheme.match_gallery) return own;
  const galleryBg = resolveGalleryBg ? resolveGalleryBg(galleryTheme) : null;
  const galleryButtonFill = resolveGalleryButtonFill ? resolveGalleryButtonFill(galleryTheme) : null;
  return {
    ...own,
    wallpaper: galleryBg || own.wallpaper,
    accent: galleryButtonFill || own.accent,
    match_gallery: true,
  };
};
